import { useState } from "react";
import {
  requestLazadaChatURL,
  requestLazadaURL,
  requestShopeeChatURL,
  requestShopeeURL,
  requestTiktokChatUrl,
  requestTiktokUrl,
} from "src/api/server/marketplace";
import { toAbsoluteUrl } from "src/resources/helpers";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConnected: () => void;
};

type MarketplaceOption = {
  id: "shopee" | "shopee-chat" | "lazada" | "lazada-chat" | "tiktok" | "tiktok-chat" | "blibli" | "bukalapak";
  label: string;
  logo: string;
  supported: boolean;
};

const options: MarketplaceOption[] = [
  { id: "shopee", label: "Shopee ERP", logo: "shopee", supported: true },
  { id: "shopee-chat", label: "Shopee Chat", logo: "shopee_chat", supported: true },
  { id: "lazada", label: "Lazada ERP", logo: "lazada", supported: true },
  { id: "lazada-chat", label: "Lazada Chat", logo: "lazada_chat", supported: true },
  { id: "tiktok", label: "TikTok Shop ERP", logo: "tiktok_shop", supported: true },
  { id: "tiktok-chat", label: "TikTok Shop Chat", logo: "tiktok_chat", supported: true },
  { id: "blibli", label: "Blibli", logo: "blibli", supported: false },
  { id: "bukalapak", label: "Bukalapak", logo: "bukalapak", supported: false },
];

async function getAuthorizationUrl(option: MarketplaceOption): Promise<string | undefined> {
  switch (option.id) {
    case "shopee": return (await requestShopeeURL("Shopee"))?.url;
    case "shopee-chat": return (await requestShopeeChatURL("Shopee_Chat"))?.url;
    case "lazada": return (await requestLazadaURL("Lazada"))?.login_url;
    case "lazada-chat": return (await requestLazadaChatURL("Lazada_Chat"))?.login_url;
    case "tiktok": return (await requestTiktokUrl("Tiktok_Shop"))?.url;
    case "tiktok-chat": return (await requestTiktokChatUrl("Tiktok_Chat"))?.url;
    default: return undefined;
  }
}

export function MarketplaceConnectModal({ isOpen, onClose, onConnected }: Props) {
  const [loadingId, setLoadingId] = useState<MarketplaceOption["id"] | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const connect = async (option: MarketplaceOption) => {
    if (!option.supported) return;
    setLoadingId(option.id);
    setError(null);
    try {
      const url = await getAuthorizationUrl(option);
      if (!url) throw new Error("Authorization URL was not returned");
      window.open(url, "_blank", "noopener,noreferrer");
      onConnected();
      onClose();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to start marketplace authorization");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-end tw-justify-center tw-bg-slate-950/40 tw-p-4 sm:tw-items-center" role="dialog" aria-modal="true" aria-labelledby="marketplace-connect-title">
      <div className="tw-w-full tw-max-w-2xl tw-rounded-lg tw-bg-white tw-shadow-xl dark:tw-bg-slate-900">
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 tw-px-5 tw-py-4 dark:tw-border-slate-700">
          <h2 id="marketplace-connect-title" className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">Connect marketplace</h2>
          <button type="button" aria-label="Close connect marketplace" onClick={onClose} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-slate-500 hover:tw-bg-slate-100 dark:hover:tw-bg-slate-800"><span className="material-symbols-outlined">close</span></button>
        </div>
        <div className="tw-p-5">
          {error && <p role="alert" className="tw-mb-4 tw-text-sm tw-text-red-600">{error}</p>}
          <div className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-3">
            {options.map((option) => <button key={option.id} type="button" disabled={!option.supported || loadingId !== null} onClick={() => void connect(option)} className="tw-flex tw-min-h-28 tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-p-3 tw-text-center tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-45 dark:tw-border-slate-700 dark:tw-text-slate-200 dark:hover:tw-bg-slate-800">
              <img src={toAbsoluteUrl(`/media/logos/marketplace/${option.logo}.png`)} alt="" className="tw-h-10 tw-w-12 tw-object-contain" />
              <span>{loadingId === option.id ? "Opening..." : option.label}</span>
              {!option.supported && <span className="tw-text-xs tw-font-normal tw-text-slate-500">Unavailable</span>}
            </button>)}
          </div>
        </div>
      </div>
    </div>
  );
}
